import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
// Frozen: the database CHECK constraints are generated from these tuples. Every value matches the
// enum helper's pattern, so none needs a slash or a space (an import file's MIME type is mapped to
// `importMediaTypes` by the API).
export const catalogItemTypes = Object.freeze([
    "DINING",
    "VILLA",
    "EVENT",
    "EXPERIENCE",
    "SERVICE",
]);
/** Only PUBLISHED items appear in normal customer search and may back a request link or a save. */
export const catalogItemStatuses = Object.freeze([
    "DRAFT",
    "REVIEW",
    "PUBLISHED",
    "UNPUBLISHED",
    "ARCHIVED",
]);
/** A price is a typed value, never a display string such as "$100 - $150". */
export const catalogPricingModels = Object.freeze([
    "UNKNOWN",
    "INFORMATION_ONLY",
    "FIXED",
    "FROM",
    "RANGE",
    "QUOTE_REQUIRED",
]);
export const catalogPriceUnits = Object.freeze([
    "PER_PERSON",
    "PER_GROUP",
    "PER_BOOKING",
    "PER_NIGHT",
    "PER_DAY",
    "PER_HOUR",
    "PER_ITEM",
]);
/** Where a price came from. Internal: never in a customer view. */
export const catalogPriceSources = Object.freeze([
    "SUPPLIER_CONTRACT",
    "SUPPLIER_PUBLISHED",
    "EDITORIAL_ESTIMATE",
    "IMPORTED",
]);
export const catalogFulfilmentModes = Object.freeze([
    "INFORMATION_ONLY",
    "HUMAN_CONCIERGE",
    "EXTERNAL_LINK",
    "CUSTOMER_SELF_SERVICE",
    "DIRECT_API",
]);
/** V1 fulfilment defaults to a human concierge. `DIRECT_API` is storable but not enabled: publishing refuses it. */
export const ENABLED_FULFILMENT_MODES = Object.freeze([
    "INFORMATION_ONLY",
    "HUMAN_CONCIERGE",
    "EXTERNAL_LINK",
    "CUSTOMER_SELF_SERVICE",
]);
export function isFulfilmentModeEnabled(mode) {
    return ENABLED_FULFILMENT_MODES.includes(mode);
}
export const catalogSourceKinds = Object.freeze([
    "EDITORIAL",
    "IMPORT",
    "PROVIDER_ENRICHMENT",
    "LEGACY_MIGRATION",
]);
export const catalogTagNamespaces = Object.freeze([
    "CUISINE",
    "DIETARY",
    "AMBIENCE",
    "MEAL_PERIOD",
    "AMENITY",
    "FEATURE",
    "RECOMMENDED_FOR",
    "GENERAL",
]);
export const catalogPersonKinds = Object.freeze(["INDIVIDUAL", "GROUP"]);
export const catalogPersonRoles = Object.freeze([
    "PERFORMER",
    "HOST",
    "CHEF",
    "GUIDE",
    "ARTIST",
    "OTHER",
]);
export const catalogMediaKinds = Object.freeze(["IMAGE", "VIDEO"]);
/** Internal note kinds. Notes live in their own append-only table and never reach a customer view. */
export const catalogNoteKinds = Object.freeze([
    "EDITORIAL",
    "SUPPLIER_CONTACT",
    "PRICING_CONTEXT",
    "FULFILMENT",
    "VERIFICATION",
]);
export const diningDressCodes = Object.freeze([
    "CASUAL",
    "SMART_CASUAL",
    "SMART",
    "FORMAL",
    "BLACK_TIE",
]);
export const diningReservationPolicies = Object.freeze([
    "REQUIRED",
    "RECOMMENDED",
    "NOT_REQUIRED",
    "WALK_IN_ONLY",
]);
export const eventStatuses = Object.freeze([
    "SCHEDULED",
    "POSTPONED",
    "CANCELLED",
    "SOLD_OUT",
]);
/**
 * How exactly an event's `starts_at` is known. `DATE_ONLY` means the source knows the date and not
 * the time: the instant is local midnight of that date in the event's zone (`ends_at`, when set, is
 * local midnight of the last date). The local date is derived from the instant and the zone, so no
 * day, month or year text is stored.
 */
export const eventSchedulePrecisions = Object.freeze(["EXACT", "DATE_ONLY"]);
export const experiencePrivacies = Object.freeze(["PRIVATE", "SHARED", "EITHER"]);
const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const SOURCE_SYSTEM = /^[a-z][a-z0-9_]{0,63}$/;
/** A URL slug: lowercase words joined by single hyphens, 1 to 120 characters. The message never echoes it. */
export function catalogSlug(value) {
    if (typeof value !== "string" || value.length < 1 || value.length > 120 || !SLUG.test(value)) {
        throw new DomainValidationError("slug", "must be lowercase words joined by single hyphens");
    }
    return value;
}
/** A source system code such as `legacy_sheet`: lowercase, starts with a letter, 1 to 64 characters. */
export function catalogSourceSystem(value) {
    if (typeof value !== "string" || !SOURCE_SYSTEM.test(value)) {
        throw new DomainValidationError("sourceSystem", "must be a lowercase system code");
    }
    return value;
}
/** A record's key inside its source system: 1 to 255 characters, no control characters. */
export function catalogSourceKey(value) {
    return boundedText("sourceKey", value, 255);
}
// --- Editing and media (Phase 3.2) -------------------------------------------------------------------
/** An item verified longer ago than this many days is "stale" in the staff list filter. */
export const CATALOG_VERIFICATION_STALE_DAYS = 180;
/** The image types a staff member may upload, with the extension of the storage key. */
export const catalogUploadContentTypes = Object.freeze({
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
});
/** One upload is 1 byte to 10 MiB. */
export const CATALOG_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
/** A new file must be attached to its item within this window of its key being made. Shorter than the sweep window. */
export const CATALOG_MEDIA_ATTACH_WINDOW_MS = 12 * 60 * 60 * 1000;
/** The sweeper removes an unreferenced file only when it is older than this. */
export const CATALOG_MEDIA_SWEEP_AGE_MS = 24 * 60 * 60 * 1000;
const UUID_PATTERN = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const MEDIA_KEY = new RegExp(`^catalog/(${UUID_PATTERN})/(${UUID_PATTERN})\\.(jpg|png|webp)$`);
/** The storage prefix of one item's files. Ends with a slash, so one id is never the prefix of another. */
export const catalogMediaPrefix = (catalogItemId) => `catalog/${catalogItemId}/`;
/** The storage key of an upload: `catalog/<catalogItemId>/<uploadId>.<jpg|png|webp>`. No URL, no bucket name. */
export function catalogMediaObjectPath(catalogItemId, uploadId, contentType) {
    return `${catalogMediaPrefix(catalogItemId)}${uploadId}.${catalogUploadContentTypes[contentType]}`;
}
/** The parts of a key made by `catalogMediaObjectPath`, or `null` for any other key (an import, a seed). */
export function parseCatalogMediaObjectPath(path) {
    const match = MEDIA_KEY.exec(path);
    if (match === null)
        return null;
    return {
        catalogItemId: match[1],
        uploadId: match[2],
        extension: match[3],
    };
}
/** The time a UUIDv7 was made (its first 48 bits, in milliseconds), or `null` when it is not a version 7 uuid. */
export function uuidV7Instant(uuid) {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(uuid)) {
        return null;
    }
    const milliseconds = Number.parseInt(uuid.replace(/-/g, "").slice(0, 12), 16);
    return new Date(milliseconds);
}
/**
 * A UUIDv7: 48 bits of the time in milliseconds, then version 7, variant 8 to b and random bits.
 * `random` needs at least 10 bytes. The time prefix makes keys sort by creation, and the orphan
 * sweep and the attach window read the time back with `uuidV7Instant`.
 */
export function makeUuidV7(at, random) {
    if (random.length < 10)
        throw new RangeError("a UUIDv7 needs 10 random bytes");
    const bytes = new Uint8Array(16);
    let time = at.getTime();
    for (let index = 5; index >= 0; index -= 1) {
        bytes[index] = time % 256;
        time = Math.floor(time / 256);
    }
    bytes[6] = 0x70 | (random[0] & 0x0f);
    bytes[7] = random[1];
    bytes[8] = 0x80 | (random[2] & 0x3f);
    for (let index = 0; index < 7; index += 1)
        bytes[9 + index] = random[3 + index];
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
